import { useEffect, useState } from 'react'
import type { Unsubscribe } from '@domain/ports/list-repository'

/**
 * Uma leitura **por assinatura**, com os três estados de `useCollection` — e
 * pelo mesmo motivo: lista vazia e lista não carregada dizem coisas diferentes
 * na tela.
 *
 * A diferença com `useCollection` é o que acontece depois do primeiro valor:
 * aqui não há `reload`, porque não há o que recarregar. A escrita local aparece
 * no snapshot seguinte sozinha, e a escrita da outra pessoa também — que é o
 * requisito da fase.
 *
 * `subscribe` precisa ser estável (`useCallback` no chamador): é ele que dispara
 * o efeito, e uma função nova a cada render abriria e fecharia a conexão
 * infinitamente.
 */
export type LiveState<T> =
  | { status: 'loading' }
  | { status: 'loaded'; items: T[] }
  | { status: 'failed' }

export function useLive<T>(
  subscribe: (onChange: (items: T[]) => void, onError: (error: unknown) => void) => Unsubscribe,
): LiveState<T> {
  const [state, setState] = useState<{ para: unknown; live: LiveState<T> }>({
    para: subscribe,
    live: { status: 'loading' },
  })

  useEffect(() => {
    const cancel = subscribe(
      (items) => {
        setState({ para: subscribe, live: { status: 'loaded', items } })
      },
      (error) => {
        // Falha de assinatura é silenciosa por natureza: sem isto, uma regra
        // negando leitura deixaria a tela num "carregando" eterno.
        console.error('useLive: a assinatura falhou', error)
        setState({ para: subscribe, live: { status: 'failed' } })
      },
    )
    return cancel
  }, [subscribe])

  // **Quem troca de assinatura volta a "carregando" AQUI, no render**, e não num
  // efeito que chama `setState`. A diferença aparece ao mudar de lista: com o
  // reset no efeito, o render entre a troca e o efeito mostra os itens da lista
  // ANTERIOR sob o nome da nova. Guardar para qual assinatura o estado vale faz
  // o dado velho ser descartado na hora, sem render intermediário — é o padrão
  // que a própria documentação do React chama de "ajustar estado ao renderizar".
  return state.para === subscribe ? state.live : { status: 'loading' }
}
